
import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTranslations } from "@/hooks/useTranslations";
import { StudentEvent } from "./types";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/components/LanguageContext";
import { LoadingButton } from "@/components/ui/loading-button";
import { useQueryClient } from "@tanstack/react-query";

interface CancelScheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: StudentEvent;
  onSuccess: (event: StudentEvent) => void;
}

export const CancelScheduleDialog = ({
  isOpen,
  onClose,
  event,
  onSuccess
}: CancelScheduleDialogProps) => {
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslations("schedule");
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const handleCancel = async () => {
    if (!event) return;
    
    setIsSubmitting(true);
    
    try {
      // If it's a request, update the request status to rejected
      if (event.type === 'request') {
        const { error } = await supabase
          .from('schedule_requests')
          .update({
            status: 'rejected', 
            cancellation_reason: reason
          })
          .eq('id', event.id);
          
        if (error) throw error;
        
        // Find and update any associated events in the schedule_events table
        // First check by related_id (direct relationship)
        const { data: relatedEvents, error: eventsError } = await supabase
          .from('schedule_events')
          .select('id')
          .eq('related_id', event.id);
          
        if (eventsError) {
          console.error('Error finding related events:', eventsError);
        } else if (relatedEvents && relatedEvents.length > 0) {
          // Update the related events to cancelled status
          const { error: updateError } = await supabase
            .from('schedule_events')
            .update({
              status: 'cancelled',
              cancellation_reason: reason,
              cancellation_date: new Date().toISOString()
            })
            .in('id', relatedEvents.map(e => e.id));
            
          if (updateError) console.error('Error updating related events:', updateError);
        }
        
        // Also check for events by student/trainer/time combination
        if (event.student_id && event.trainer_id && event.start_time) {
          const { data: timeEvents, error: timeEventsError } = await supabase
            .from('schedule_events')
            .select('id')
            .eq('student_id', event.student_id)
            .eq('trainer_id', event.trainer_id)
            .eq('start_time', event.start_time);
            
          if (timeEventsError) {
            console.error('Error finding time-related events:', timeEventsError);
          } else if (timeEvents && timeEvents.length > 0) {
            // Update the time-related events to cancelled status
            const { error: updateTimeError } = await supabase
              .from('schedule_events')
              .update({
                status: 'cancelled',
                cancellation_reason: reason,
                cancellation_date: new Date().toISOString()
              })
              .in('id', timeEvents.map(e => e.id));
              
            if (updateTimeError) console.error('Error updating time-related events:', updateTimeError);
          }
        }
      } 
      // If it's a scheduled event, update its status
      else if (event.type === 'scheduled') {
        const { error } = await supabase
          .from('schedule_events')
          .update({
            status: 'cancelled',
            cancellation_reason: reason,
            cancellation_date: new Date().toISOString()
          })
          .eq('id', event.id);
          
        if (error) throw error;
        
        // Check for any associated request that might need to be updated
        if (event.related_id) {
          const { error: requestError } = await supabase
            .from('schedule_requests')
            .update({
              status: 'rejected',
              cancellation_reason: reason
            })
            .eq('id', event.related_id);
            
          if (requestError) console.error('Error updating related request:', requestError);
        }
        
        // Also check for related requests by student/time
        if (event.student_id && event.start_time) {
          const { data: relatedRequests, error: requestsError } = await supabase
            .from('schedule_requests')
            .select('id')
            .eq('student_id', event.student_id)
            .eq('requested_date', event.start_time);
            
          if (requestsError) {
            console.error('Error finding related requests:', requestsError);
          } else if (relatedRequests && relatedRequests.length > 0) {
            const { error: updateRequestsError } = await supabase
              .from('schedule_requests')
              .update({
                status: 'rejected',
                cancellation_reason: reason
              })
              .in('id', relatedRequests.map(r => r.id));
              
            if (updateRequestsError) console.error('Error updating related requests:', updateRequestsError);
          }
        }
      }
      
      // Create a copy of the event with updated status for local UI update
      const updatedEvent: StudentEvent = {
        ...event,
        status: 'cancelled',
        cancellation_reason: reason,
        cancellation_date: new Date().toISOString()
      };
      
      toast.success(language === 'pt' ? 'Aula cancelada com sucesso' : 'Class cancelled successfully');
      
      // Immediately update the UI
      onSuccess(updatedEvent);
      
      // Invalidate queries to ensure data is refreshed
      queryClient.invalidateQueries();
      
      onClose();
    } catch (error) {
      console.error('Error cancelling event:', error);
      toast.error(language === 'pt' ? 'Erro ao cancelar aula' : 'Error cancelling class');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="dropdown-opaque">
        <DialogHeader>
          <DialogTitle>{t("cancelDialog.title")}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 my-4">
          <p>{t("cancelDialog.description")}</p>
          
          <div className="space-y-2">
            <Label htmlFor="reason">{t("cancelDialog.reasonLabel")}</Label>
            <Textarea 
              id="reason" 
              placeholder={t("cancelDialog.reasonPlaceholder")}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t("cancelDialog.cancelButton")}
          </Button>
          <LoadingButton 
            onClick={handleCancel} 
            loading={isSubmitting}
            variant="destructive"
          >
            {t("cancelDialog.confirmButton")}
          </LoadingButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
